import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShieldAlert } from "lucide-react";
import { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import {
  Field,
  TextInput,
  SelectInput,
  TextArea,
  CheckboxRow,
  FormNotice,
} from "@/components/FormControls";
import api, { formatApiErrorDetail } from "@/lib/api";
import { planFromId, formatPrice, includesBreakfast, includesLunch } from "@/lib/plans";
import { useBrand } from "@/context/BrandContext";

const initial = {
  parent_name: "",
  email: "",
  mobile: "",
  delivery_mode: "home",
  pincode: "",
  address: "",
  school: "",
  start_date: "",
  coupon_code: "",
  referral_code: "",
  allergy_acknowledged: false,
  notes: "",
  child: {
    name: "",
    age: "",
    age_group: "2-4",
    school: "",
    vegetarian: true,
    allergies: "",
    allergy_severity: "",
    restrictions: "",
    dislikes: "",
    spice_preference: "mild",
  },
};

export default function CheckoutPage() {
  const brand = useBrand();
  const [params] = useSearchParams();
  const plan = planFromId(params.get("plan"));

  const [form, setForm] = useState({ ...initial, delivery_mode: plan.deliveryModes[0] });
  const [areas, setAreas] = useState([]);
  const [state, setState] = useState({ status: "idle", message: "", tone: "info" });

  useEffect(() => {
    api
      .get("/serviceability/areas")
      .then((r) => setAreas(r.data.areas.filter((a) => a.status === "active")))
      .catch(() => setAreas([]));
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setChild = (k) => (e) =>
    setForm((f) => ({
      ...f,
      child: { ...f.child, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value },
    }));

  const selectedArea = areas.find((a) => a.pincode === form.pincode);

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "", tone: "info" });
    try {
      const { data } = await api.post("/orders", { ...form, plan_id: plan.id });
      setState({
        status: "done",
        tone: data.order.allergy_review === "requires_discussion" ? "warn" : "info",
        message: `${data.message} Reference: ${data.order.order_id}`,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setState({
        status: "error",
        tone: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Checkout"
        title={`Set up your ${plan.typeLabel.toLowerCase()} plan`}
        intro="Tell us about your child, confirm delivery and we will take it from there. Nothing is charged on this page."
      />

      <section className="py-14 sm:py-20">
        <div className="mbm-container grid gap-10 lg:grid-cols-[1fr_0.7fr] lg:items-start">
          <Reveal>
            {state.status === "done" ? (
              <div className="mbm-card p-6 sm:p-8" data-testid="checkout-success">
                <Check className="h-7 w-7 text-coralInk" aria-hidden="true" />
                <h2 className="mt-4 font-heading text-xl font-semibold">Request received</h2>
                <p className="mt-3 text-sm leading-relaxed text-cocoa/70">{state.message}</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <CTAButton to="/meals" data-testid="checkout-view-menu">
                    View the meals
                  </CTAButton>
                  <CTAButton to="/account" variant="outline" data-testid="checkout-account">
                    Go to my account
                  </CTAButton>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-6" data-testid="checkout-form">
                <div className="mbm-card p-6 sm:p-8">
                  <h2 className="font-heading text-lg font-semibold">1. Your details</h2>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <Field label="Parent full name" required>
                      <TextInput required value={form.parent_name} onChange={set("parent_name")} data-testid="co-parent-name" />
                    </Field>
                    <Field label="Mobile number" required>
                      <TextInput required type="tel" value={form.mobile} onChange={set("mobile")} data-testid="co-mobile" />
                    </Field>
                    <Field label="Email" required className="sm:col-span-2">
                      <TextInput required type="email" value={form.email} onChange={set("email")} data-testid="co-email" />
                    </Field>
                  </div>
                </div>

                <div className="mbm-card p-6 sm:p-8">
                  <h2 className="font-heading text-lg font-semibold">2. Child profile</h2>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <Field label="Child’s name" required>
                      <TextInput required value={form.child.name} onChange={setChild("name")} data-testid="co-child-name" />
                    </Field>
                    <Field label="Age">
                      <TextInput value={form.child.age} onChange={setChild("age")} data-testid="co-child-age" />
                    </Field>
                    <Field label="Portion / age group" required>
                      <SelectInput required value={form.child.age_group} onChange={setChild("age_group")} data-testid="co-age-group">
                        {brand.ageRanges.map((a) => (
                          <option key={a.id} value={a.id}>{`${a.label} — ${a.portion}`}</option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Spice preference">
                      <SelectInput value={form.child.spice_preference} onChange={setChild("spice_preference")} data-testid="co-spice">
                        <option value="very-mild">Very mild</option>
                        <option value="mild">Mild</option>
                        <option value="medium">Medium</option>
                      </SelectInput>
                    </Field>
                    <Field label="Dietary restrictions">
                      <TextInput value={form.child.restrictions} onChange={setChild("restrictions")} data-testid="co-restrictions" />
                    </Field>
                    <Field label="Food dislikes">
                      <TextInput value={form.child.dislikes} onChange={setChild("dislikes")} data-testid="co-dislikes" />
                    </Field>
                  </div>
                </div>

                <div className="mbm-card p-6 sm:p-8">
                  <h2 className="font-heading text-lg font-semibold">3. Allergy and dietary safety</h2>
                  <p className="mt-2 text-sm leading-relaxed text-cocoa/70">
                    This information is required before any order is confirmed.
                  </p>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Does your child have any food allergies?"
                      hint="Write ‘none’ if there are none."
                      required
                      className="sm:col-span-2"
                    >
                      <TextInput
                        required
                        value={form.child.allergies}
                        onChange={setChild("allergies")}
                        data-testid="co-allergies"
                        placeholder="e.g. peanuts, sesame — or none"
                      />
                    </Field>
                    <Field label="Severity">
                      <SelectInput value={form.child.allergy_severity} onChange={setChild("allergy_severity")} data-testid="co-severity">
                        <option value="">Not applicable</option>
                        <option value="mild">Mild</option>
                        <option value="moderate">Moderate</option>
                        <option value="severe">Severe</option>
                        <option value="life-threatening">Life-threatening</option>
                      </SelectInput>
                    </Field>
                    <Field label="Vegetarian preference">
                      <SelectInput
                        value={form.child.vegetarian ? "yes" : "no"}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            child: { ...f.child, vegetarian: e.target.value === "yes" },
                          }))
                        }
                        data-testid="co-vegetarian"
                      >
                        <option value="yes">Vegetarian</option>
                        <option value="no">Not strictly vegetarian</option>
                      </SelectInput>
                    </Field>
                    <div className="sm:col-span-2">
                      <CheckboxRow
                        label="I understand that I must contact the Mothers by Mothers team before ordering if my child has a severe or life-threatening allergy."
                        checked={form.allergy_acknowledged}
                        onChange={set("allergy_acknowledged")}
                        data-testid="co-allergy-ack"
                      />
                    </div>
                  </div>
                </div>

                <div className="mbm-card p-6 sm:p-8">
                  <h2 className="font-heading text-lg font-semibold">4. Delivery</h2>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <Field label="Delivery type" required>
                      <SelectInput required value={form.delivery_mode} onChange={set("delivery_mode")} data-testid="co-delivery-mode">
                        <option value="home">Home delivery</option>
                        {plan.deliveryModes.includes("school") && (
                          <option value="school">School delivery</option>
                        )}
                      </SelectInput>
                    </Field>
                    <Field label="Pincode" required>
                      <SelectInput required value={form.pincode} onChange={set("pincode")} data-testid="co-pincode">
                        <option value="">Select your pincode</option>
                        {areas.map((a) => (
                          <option key={a.pincode} value={a.pincode}>{`${a.pincode} — ${a.area}`}</option>
                        ))}
                      </SelectInput>
                    </Field>
                    {form.delivery_mode === "school" && (
                      <Field label="School" required className="sm:col-span-2">
                        <SelectInput required value={form.school} onChange={set("school")} data-testid="co-school">
                          <option value="">Select a school</option>
                          {(selectedArea?.schools || []).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </SelectInput>
                      </Field>
                    )}
                    {form.delivery_mode === "home" && (
                      <Field label="Delivery address" required className="sm:col-span-2">
                        <TextArea required rows={3} value={form.address} onChange={set("address")} data-testid="co-address" />
                      </Field>
                    )}
                    <Field label="Preferred start date" required>
                      <TextInput required type="date" value={form.start_date} onChange={set("start_date")} data-testid="co-start-date" />
                    </Field>
                    <Field label="Coupon or referral code">
                      <TextInput value={form.coupon_code} onChange={set("coupon_code")} data-testid="co-coupon" />
                    </Field>
                    <Field label="Notes for our team" className="sm:col-span-2">
                      <TextArea rows={3} value={form.notes} onChange={set("notes")} data-testid="co-notes" />
                    </Field>
                  </div>
                </div>

                {state.message && (
                  <FormNotice tone={state.tone} testId="checkout-error">
                    {state.message}
                  </FormNotice>
                )}

                <CTAButton type="submit" disabled={state.status === "loading"} data-testid="checkout-submit">
                  {state.status === "loading" ? "Sending…" : "Confirm subscription request"}
                </CTAButton>
              </form>
            )}
          </Reveal>

          <Reveal delay={120} className="lg:sticky lg:top-28">
            <div className="mbm-card p-6">
              <p className="mbm-eyebrow">Order summary</p>
              <h2 className="mt-2 font-heading text-lg font-semibold">{plan.typeLabel}</h2>
              <p className="mt-1 text-[13px] font-medium text-rust">
                {plan.durationLabel} · {plan.weeks === 1 ? "1 week" : `${plan.weeks} weeks`} ·{" "}
                {plan.totalMeals} meals
              </p>
              <p className="mt-3 text-sm leading-relaxed text-cocoa/70">{plan.summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {includesBreakfast(plan) && <span className="mbm-chip">Breakfast included</span>}
                {includesLunch(plan) && <span className="mbm-chip">Lunch included</span>}
              </div>
              <ul className="mt-5 space-y-2 border-t border-line pt-5 text-[13px] text-cocoa/70">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-[14px] bg-sageSoft p-4 text-sm ring-1 ring-forest/10">
                <p className="font-heading text-lg font-bold text-cocoa">
                  {formatPrice(plan)}
                  {plan.price ? ` / ${plan.cadence}` : ""}
                </p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-cocoa/75">{plan.priceNote}</p>
              </div>
              <Link
                to="/plans"
                data-testid="checkout-change-plan"
                className="mt-4 inline-flex min-h-[44px] items-center text-[13px] font-extrabold text-coralInk underline underline-offset-4 hover:text-rust"
              >
                Change plan
              </Link>
              <div className="mt-5 flex gap-3 rounded-[24px] bg-amber/45 p-4 text-[13px] leading-relaxed text-cocoa">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-rust" aria-hidden="true" />
                <p>
                  {brand.payments.note}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
